import { motion } from 'framer-motion';
import { ArrowRight, Clock } from 'lucide-react';

const insights = [
  {
    type: 'Blog',
    title: 'Why 95% of AI Pilots Fail (And How to Be in the 5%)',
    preview: 'Most AI initiatives stall because companies treat AI as a project instead of a capability. Here is what works.',
    href: '/insights/ai-pilots-fail',
    img: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop',
    readTime: '6 min read',
  },
  {
    type: 'Whitepaper',
    title: 'The Build + Protect Playbook: Integrating Security into Your Development Lifecycle',
    preview: 'A practical framework for embedding security at every stage of software delivery.',
    href: '/insights',
    img: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=600&h=400&fit=crop',
    readTime: '12 min read',
  },
  {
    type: 'Blog',
    title: 'What to Look for in a Technology Partner (A CTO\'s Honest Checklist)',
    preview: 'Evaluating technology partners is hard. Here are the questions that actually matter.',
    href: '/insights/cto-checklist-technology-partner',
    img: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=400&fit=crop',
    readTime: '5 min read',
  },
];

export default function InsightsPreview() {
  return (
    <section data-testid="insights-section" className="bg-navy-50 py-16 lg:py-20">
      <div className="max-w-content mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-10"
        >
          <div>
            <p className="text-overline text-orange-500 mb-3">INSIGHTS</p>
            <h2 data-testid="insights-heading" className="text-h2 text-navy-800 font-black tracking-tight mb-2">Ideas That Drive Decisions</h2>
            <p className="text-navy-500 text-base max-w-xl">Practical perspectives from 20+ years of building and protecting technology.</p>
          </div>
          <a
            href="/insights"
            data-testid="explore-insights-link"
            className="group text-orange-500 text-sm font-medium inline-flex items-center gap-1.5 hover:gap-2.5 transition-all mt-4 sm:mt-0 flex-shrink-0"
          >
            View All <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
        </motion.div>

        {/* Editorial cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {insights.map((item, i) => (
            <motion.a
              key={item.title}
              href={item.href}
              data-testid={`insight-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="group bg-white rounded-xl overflow-hidden border border-navy-100 hover:border-orange-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            >
              {/* Image */}
              <div className="relative h-44 overflow-hidden">
                <img
                  src={item.img}
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3">
                  <span className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full backdrop-blur-sm ${
                    item.type === 'Whitepaper'
                      ? 'bg-navy-800/80 text-white'
                      : 'bg-orange-500/90 text-white'
                  }`}>
                    {item.type}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-navy-800 font-semibold text-base mb-2 leading-snug group-hover:text-orange-600 transition-colors duration-200 line-clamp-2">
                  {item.title}
                </h3>
                <p className="text-navy-500 text-sm leading-relaxed mb-4 line-clamp-2">{item.preview}</p>
                <div className="flex items-center justify-between">
                  <span className="text-navy-400 text-xs inline-flex items-center gap-1">
                    <Clock size={12} /> {item.readTime}
                  </span>
                  <span className="text-orange-500 text-xs font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read <ArrowRight size={12} />
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
